import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { routePathName } from '@app/app.models';
import { IUser, UserService } from '@app/services/user';
import { I18nService } from '@localize/services/';
import { AuthService } from '@services/auth/';
import { ProgressIndicatorService } from '@services/progress/';
import { SystemAlertId, SystemAlertsService } from '@services/system-alerts/';
import { Subscription } from 'rxjs';

import { externalLink, ObjectString } from '../../../shared/utilities';
import { BroadcastKey, BroadcastService } from '../../services/broadcast';
import { PermissionCognitoGroups, TokenService } from '../../services/token';
import { IMainMenuItem } from './manager.models';

@Component({
	selector: 'app-manager',
	templateUrl: './manager.component.html',
	styleUrls: ['./manager.component.scss']
})
export class ManagerComponent implements OnInit, OnDestroy, AfterViewInit {
	public mainMenuItems: IMainMenuItem[] = [
		{
			name: this.i18nService.get('Documentation', 'app.descriptor.documentation'),
			path: routePathName.apiDocumentation,
			alertIds: [],
			class: 'js-main-menu-documentation'
		}
	];
	public myAccountItem: IMainMenuItem = {
		path: '/' + routePathName.account,
		alertIds: [SystemAlertId.emailNotVerified]
	};

	public currentDate = new Date();
	private userDataObs: Subscription;
	private broadcastObs: Subscription;
	public accountUserFullname = '';
	public hasAlerts = false;
	public externalLink = externalLink;

	constructor(
		private authService: AuthService,
		private progressIndicatorService: ProgressIndicatorService,
		private i18nService: I18nService,
		private userService: UserService,
		public systemAlerts: SystemAlertsService,
		private broadcastService: BroadcastService,
		private tokenService: TokenService
	) {}

	public ngOnInit() {
		this.enableProtectedMenuItems();

		this.userDataObs = this.userService.dataObs.subscribe((users: ObjectString<IUser>) => {
			if (users[this.authService.accountUser?.id]) {
				this.accountUserFullname = this.getAccountUserFullname();
			}
		});

		this.hasAlerts = this.systemAlerts.hasAlerts();

		this.broadcastObs = this.broadcastService
			.on(BroadcastKey.system_alert_cancelled)
			.subscribe(() => (this.hasAlerts = this.systemAlerts.hasAlerts()));

		this.progressIndicatorService.spinner(false);
	}

	public ngOnDestroy() {
		this.userDataObs?.unsubscribe();
		this.broadcastObs?.unsubscribe();
	}

	public ngAfterViewInit(): void {
		setTimeout(() => {
			this.accountUserFullname = this.getAccountUserFullname();
		});
	}

	public logout(): void {
		this.authService.logout();
	}

	private enableProtectedMenuItems() {

		this.tokenService.getDecodedToken().subscribe((decodedToken) => {

			if (decodedToken['roles'].includes(PermissionCognitoGroups.appAdmin) ||
				decodedToken['roles'].includes(PermissionCognitoGroups.systemAdmin)) {
				this.addMenuItem(0, {
					name: this.i18nService.get('App Admin', 'app.descriptor.appadmin'),
					path: routePathName.apps,
					alertIds: [],
					class: 'js-main-menu-companies'
				});
			}

			if (decodedToken['roles'].includes(PermissionCognitoGroups.registrations)) {
				this.addMenuItem(1, {
					name: this.i18nService.get('Registrations', 'app.descriptor.registrations'),
					path: routePathName.registrations,
					alertIds: [],
					class: 'js-main-menu-registrations'
				});
			}

			if (decodedToken['roles'].includes(PermissionCognitoGroups.userAdmin) ||
				decodedToken['roles'].includes(PermissionCognitoGroups.systemAdmin)) {
				this.addMenuItem(1, {
					name: this.i18nService.get('Users', 'app.descriptor.users'),
					path: routePathName.users,
					alertIds: [],
					class: 'js-main-menu-users'
				});
			}
		});
	}

	private addMenuItem(position: number, menuItem: IMainMenuItem): void {
		this.mainMenuItems.splice(position, 0, menuItem);
	}

	private getAccountUserFullname(): string {
		return this.authService.accountUser ? `${this.authService.accountUser.firstname} ${this.authService.accountUser.lastname}` : '';
	}
}
